import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Snackbar
} from '@mui/material';
import {
  Info as InfoIcon,
  Devices as DeviceIcon,
  TableRestaurant as TableIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);
const MotionAccordion = motion(Accordion);

const CartMetadataManager = ({ settings, updateCartMetadata }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // ✅ NEW: Auto-save feedback state
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // ✅ FIXED: Consistent field order - prevents reordering
  const FIELD_ORDER = ['deviceNumber', 'tableNumber', 'customerName', 'notes'];

  const getFieldIcon = (fieldName) => {
    switch (fieldName) {
      case 'deviceNumber': return <DeviceIcon />;
      case 'tableNumber': return <TableIcon />;
      case 'customerName': return <PersonIcon />;
      case 'notes': return <NotesIcon />;
      default: return <InfoIcon />;
    }
  };

  const getFieldColor = (fieldName) => {
    switch (fieldName) {
      case 'deviceNumber': return '#667eea';
      case 'tableNumber': return '#10b981';
      case 'customerName': return '#f59e0b';
      case 'notes': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  // ✅ ENHANCED: Auto-save with debouncing
  const handleFieldUpdate = useCallback(async (fieldName, updates) => {
    if (typeof updateCartMetadata !== 'function') {
      console.error('❌ updateCartMetadata is not a function:', typeof updateCartMetadata);
      return;
    }
    
    try {
      setAutoSaveStatus('saving');
      
      const currentField = settings?.cartMetadata?.[fieldName] || {};
      const updatedField = { 
        ...currentField, 
        ...updates,
        enabled: updates.enabled !== undefined ? updates.enabled : currentField.enabled || false,
        visible: updates.visible !== undefined ? updates.visible : (currentField.visible !== false), // Default to true
        required: updates.required !== undefined ? updates.required : currentField.required || false
      };
      
      console.log('🔄 Auto-saving cart metadata field:', { fieldName, currentField, updates, updatedField });
      
      // ✅ CRITICAL: Call update without page refresh
      await updateCartMetadata(fieldName, updatedField);
      
      setAutoSaveStatus('saved');
      setShowSaveSuccess(true);
      
      // Hide success message after 2 seconds
      setTimeout(() => {
        setAutoSaveStatus('');
        setShowSaveSuccess(false);
      }, 2000);
      
      console.log('✅ Auto-save successful');
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus(''), 3000);
    }
  }, [settings?.cartMetadata, updateCartMetadata]);

  // ✅ FIXED: Use consistent field order
  const orderedMetadataFields = FIELD_ORDER.map(fieldName => [
    fieldName, 
    settings?.cartMetadata?.[fieldName] || {}
  ]).filter(([, config]) => Object.keys(config).length > 0);

  if (orderedMetadataFields.length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('posConfig.cartMetadata.title') || 'Cart Metadata Fields'}
        </Typography>
        <Alert severity="info">
          {t('posConfig.cartMetadata.noMetadataFields') || 'No metadata fields configured. Contact your system administrator to set up metadata fields.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Auto-Save Status */}
      <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#333' }}>
            {t('posConfig.cartMetadata.title') || 'Cart Metadata Fields'}
          </Typography>
          
          {/* ✅ NEW: Auto-Save Status Indicator */}
          {autoSaveStatus && (
            <Chip
              label={
                autoSaveStatus === 'saving' ? 'Auto-saving...' :
                autoSaveStatus === 'saved' ? 'Auto-saved ✓' :
                autoSaveStatus === 'error' ? 'Save failed ✗' : ''
              }
              color={
                autoSaveStatus === 'saving' ? 'primary' :
                autoSaveStatus === 'saved' ? 'success' :
                autoSaveStatus === 'error' ? 'error' : 'default'
              }
              size="small"
              sx={{ 
                fontWeight: 600,
                animation: autoSaveStatus === 'saving' ? 'pulse 1.5s infinite' : 'none'
              }}
            />
          )}
        </Box>
        
        <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
          {t('posConfig.cartMetadata.description') || 'Configure additional information fields for orders. Changes are automatically saved.'}
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: isMobile ? 2 : 3,
          borderRadius: 2,
          fontSize: isMobile ? '0.8rem' : '0.875rem'
        }}
      >
        {t('posConfig.cartMetadata.infoAlert') || 'These fields will appear in the cart section of your POS system when enabled and visible. All changes are auto-saved.'}
      </Alert>

      {/* ✅ MOBILE: Accordion Layout with FIXED ORDER */}
      {isMobile ? (
        <Stack spacing={1}>
          {orderedMetadataFields.map(([fieldName, fieldConfig], index) => {
            const fieldColor = getFieldColor(fieldName);
            return (
              <MotionAccordion
                key={`${fieldName}-${index}`} // ✅ Stable key
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                sx={{
                  background: 'white',
                  border: `1px solid ${fieldColor}20`,
                  borderLeft: `4px solid ${fieldColor}`,
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&.Mui-expanded': {
                    boxShadow: `0 4px 16px ${fieldColor}25`
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    py: 1,
                    '& .MuiAccordionSummary-content': { 
                      alignItems: 'center',
                      my: 1
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: `${fieldColor}15`, color: fieldColor }}>
                      {React.cloneElement(getFieldIcon(fieldName), { sx: { fontSize: 16 } })}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333' }}>
                        {fieldConfig.label || fieldName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {fieldConfig.enabled && (
                          <Chip
                            size="small"
                            label="Enabled"
                            color="success"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                          />
                        )}
                        {fieldConfig.required && (
                          <Chip
                            size="small"
                            label="Required"
                            color="warning"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                          />
                        )}
                        {fieldConfig.visible && fieldConfig.enabled && (
                          <Chip
                            size="small"
                            label="Visible in POS"
                            color="info"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {fieldConfig.enabled ? <CheckIcon sx={{ color: '#10b981', fontSize: 16 }} /> : <UncheckIcon sx={{ color: '#6b7280', fontSize: 16 }} />}
                    </Box>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                  <Stack spacing={2}>
                    {/* Toggle Controls */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#333' }}>
                        Enable Field
                      </Typography>
                      <Switch
                        checked={fieldConfig.enabled || false}
                        onChange={(e) => handleFieldUpdate(fieldName, { enabled: e.target.checked })}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: fieldColor,
                            '& + .MuiSwitch-track': { backgroundColor: fieldColor }
                          }
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: fieldConfig.enabled ? '#333' : '#9ca3af' }}>
                        Show in POS
                      </Typography>
                      <Switch
                        checked={fieldConfig.visible !== false}
                        onChange={(e) => handleFieldUpdate(fieldName, { visible: e.target.checked })}
                        disabled={!fieldConfig.enabled}
                        size="small"
                        color="primary"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: fieldColor,
                            '& + .MuiSwitch-track': { backgroundColor: fieldColor }
                          }
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: fieldConfig.enabled ? '#333' : '#9ca3af' }}>
                        Required Field
                      </Typography>
                      <Switch
                        checked={fieldConfig.required || false}
                        onChange={(e) => handleFieldUpdate(fieldName, { required: e.target.checked })}
                        disabled={!fieldConfig.enabled}
                        size="small"
                        color="warning"
                      />
                    </Box>

                    {/* Text Inputs */}
                    <TextField
                      fullWidth
                      label="Display Label"
                      value={fieldConfig.label || ''}
                      onChange={(e) => handleFieldUpdate(fieldName, { label: e.target.value })}
                      disabled={!fieldConfig.enabled}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {React.cloneElement(getFieldIcon(fieldName), { 
                              sx: { fontSize: 18, color: fieldConfig.enabled ? fieldColor : '#9ca3af' } 
                            })}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: fieldColor
                        },
                        '& .MuiInputLabel-root.Mui-focused': { color: fieldColor }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Placeholder Text"
                      value={fieldConfig.placeholder || ''}
                      onChange={(e) => handleFieldUpdate(fieldName, { placeholder: e.target.value })}
                      disabled={!fieldConfig.enabled}
                      size="small"
                      helperText="Text shown when field is empty"
                      sx={{
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: fieldColor
                        },
                        '& .MuiInputLabel-root.Mui-focused': { color: fieldColor }
                      }}
                    />

                    {/* Live Preview */}
                    {fieldConfig.enabled && fieldConfig.visible && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: `${fieldColor}08`, borderRadius: 2, border: `1px solid ${fieldColor}20` }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                          📱 POS Preview:
                        </Typography>
                        <TextField
                          fullWidth
                          label={fieldConfig.label || fieldName}
                          placeholder={fieldConfig.placeholder || `Enter ${fieldName}...`}
                          size="small"
                          disabled
                          required={fieldConfig.required}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {React.cloneElement(getFieldIcon(fieldName), { sx: { fontSize: 16, color: fieldColor } })}
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                        />
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </MotionAccordion>
            );
          })}
        </Stack>
      ) : (
        /* ✅ TABLET/DESKTOP: Fixed Grid Layout */
        <Grid container spacing={isTablet ? 2 : 3}>
          {orderedMetadataFields.map(([fieldName, fieldConfig], index) => {
            const fieldColor = getFieldColor(fieldName);
            return (
              <Grid item xs={12} md={6} key={`${fieldName}-${index}`}> {/* ✅ Stable key */}
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  sx={{
                    height: '100%',
                    border: `1px solid ${fieldColor}20`,
                    borderLeft: `4px solid ${fieldColor}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 8px 24px ${fieldColor}25`,
                      borderColor: `${fieldColor}30`
                    }
                  }}
                  whileHover={{ y: -2, scale: 1.01 }}
                >
                  <CardContent sx={{ p: isTablet ? 2 : 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 40, height: 40, bgcolor: `${fieldColor}15`, color: fieldColor }}>
                          {getFieldIcon(fieldName)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#333' }}>
                            {fieldConfig.label || fieldName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {fieldConfig.enabled && (
                              <Chip
                                label="Enabled"
                                color="success"
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {fieldConfig.required && (
                              <Chip
                                label="Required"
                                color="warning"
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {fieldConfig.visible && fieldConfig.enabled && (
                              <Chip
                                label="Visible in POS"
                                color="info"
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* ✅ FIXED: Visibility Toggle Button with span wrapper */}
                      <Tooltip title={fieldConfig.visible && fieldConfig.enabled ? 'Hide from POS' : 'Show in POS'}>
                        <span>
                          <IconButton
                            onClick={() => handleFieldUpdate(fieldName, { visible: !fieldConfig.visible })}
                            color={fieldConfig.visible && fieldConfig.enabled ? 'primary' : 'default'}
                            size="small"
                            disabled={!fieldConfig.enabled}
                            sx={{ color: fieldConfig.enabled ? fieldColor : '#9ca3af' }}
                          >
                            {fieldConfig.visible && fieldConfig.enabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    {/* Controls */}
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={fieldConfig.enabled || false}
                            onChange={(e) => handleFieldUpdate(fieldName, { enabled: e.target.checked })}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: fieldColor,
                                '& + .MuiSwitch-track': { backgroundColor: fieldColor }
                              }
                            }}
                          />
                        }
                        label="Enabled"
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={fieldConfig.required || false}
                            onChange={(e) => handleFieldUpdate(fieldName, { required: e.target.checked })}
                            color="warning"
                            disabled={!fieldConfig.enabled}
                          />
                        }
                        label="Required"
                      />

                      <TextField
                        fullWidth
                        label="Display Label"
                        value={fieldConfig.label || ''}
                        onChange={(e) => handleFieldUpdate(fieldName, { label: e.target.value })}
                        disabled={!fieldConfig.enabled}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {React.cloneElement(getFieldIcon(fieldName), { 
                                sx: { fontSize: 18, color: fieldConfig.enabled ? fieldColor : '#9ca3af' } 
                              })}
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldColor
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: fieldColor }
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Placeholder Text"
                        value={fieldConfig.placeholder || ''}
                        onChange={(e) => handleFieldUpdate(fieldName, { placeholder: e.target.value })}
                        disabled={!fieldConfig.enabled}
                        size="small"
                        helperText="Text shown when field is empty"
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldColor
                          },
                          '& .MuiInputLabel-root.Mui-focused': { color: fieldColor }
                        }}
                      />
                    </Stack>

                    {/* Live Preview */}
                    {fieldConfig.enabled && fieldConfig.visible && (
                      <Box sx={{ mt: 3, p: 2, bgcolor: `${fieldColor}08`, borderRadius: 2, border: `1px solid ${fieldColor}20` }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                          POS Preview:
                        </Typography>
                        <TextField
                          fullWidth
                          label={fieldConfig.label || fieldName}
                          placeholder={fieldConfig.placeholder || `Enter ${fieldName}...`}
                          size="small"
                          disabled
                          required={fieldConfig.required}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {React.cloneElement(getFieldIcon(fieldName), { sx: { fontSize: 16, color: fieldColor } })}
                              </InputAdornment>
                            ),
                          }}
                          sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </MotionCard>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Usage Examples */}
      <Paper sx={{ 
        mt: isMobile ? 3 : 4, 
        p: isMobile ? 2 : 3,
        bgcolor: 'primary.lighter',
        borderRadius: 3,
        border: '1px solid rgba(102, 126, 234, 0.1)'
      }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
          Usage Examples
        </Typography>
        <Grid container spacing={isMobile ? 1 : 2}>
          {[
            {
              title: 'Device Number',
              desc: 'Track which device/register the order was placed from'
            },
            {
              title: 'Table Number',
              desc: 'For dine-in orders, specify which table the order belongs to'
            },
            {
              title: 'Customer Name',
              desc: 'Collect customer name for pickup orders or personalization'
            },
            {
              title: 'Order Notes',
              desc: 'Allow staff to add special instructions or notes to orders'
            }
          ].map((example, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ mb: isMobile ? 1 : 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                  {example.title}
                </Typography>
                <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {example.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* ✅ AUTO-SAVE SUCCESS SNACKBAR */}
      <Snackbar
        open={showSaveSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSaveSuccess(false)}
          severity="success"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontWeight: 600
          }}
        >
          ✅ Auto-saved successfully!
        </Alert>
      </Snackbar>

      {/* ✅ FIXED: Regular style tag without jsx attribute */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          .pulse-animation {
            animation: pulse 2s infinite;
          }
        `}
      </style>
    </Box>
  );
};

export default CartMetadataManager;